/* eslint-disable import/no-unresolved */
import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { LoginComponent } from 'assets/components';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { OnboardingContext } from '../../../contexts/onboarding/context';
import { ACCOUNT_REGIONS } from '../../../utilities/constants';
import {
  encryptWithPublicKey,
  getUrlEnvironment,
  removeWhitespaces,
} from '../../../utilities/helpers';
import { showAlert } from '../../../utilities/notifications';
import { FLOWS, aliasUser, identifyUser, sendStats } from '../../../services/mixpanel';
import {
  cacheRecaptchaV3failure,
  clearRecaptchaV3failure,
  getCacheRecaptchaV3failure,
  removeCachedPartialFlowToken,
  resetUserSession,
} from '../../../utilities/caching';
import { validateEmail } from '../../../utilities/formValidation';
import { clearFormError, handleFormBlur } from '../../../utilities/formsUtils';
import { USER_LOGIN } from '../mutations';
import {
  MXP_ONBOARDING_SIGN_UP_CREATE_ACCOUNT,
  MXP_RESET_PASSWORD_VISIT,
  MXP_SIGN_IN_SUCCESS,
  MXP_SIGN_IN_VISIT,
} from '../mixpanel-events';
import { PARTIAL_SIGN_UP_PHASES, RECAPTCHA_EVENTS } from '../constants';
import { revealPassword } from '../helpers';
import { PASSWORD_PUBLIC_KEY } from '../../../utilities/sharedQueries';
import { useSetting } from '../../../utilities/sharedHooks/settings';
import RecaptchaModal from '../../shared/RecaptchaModal';
import { useUsEnv } from '../../../utilities/sharedHooks/useUsEnv';
import { OAUTH_SESSION_KEYS } from '../../Oauth/constants';
import { WITHDRAWAL_DEEPLINK_SESSION_KEYS } from '../../Accounts/deeplink/constants';
import { TOPUP_DEEPLINK_SESSION_KEYS } from '../../Accounts/topupDeeplink/constants';
import { CASHBACK_DEEPLINK_SESSION_KEYS } from '../../Card/Loyalty/deeplink/constants';
import { SET_PIN_DEEPLINK_SESSION_KEYS } from '../../Card/Settings/deeplink/constants';
import { MANAGE_ASSETS_DEEPLINK_SESSION_KEYS } from '../../Accounts/manageAssetsDeeplink/constants';
import { CARD_ORDER_DEEPLINK_SESSION_KEYS } from '../../Card/deeplink/constants';

const Login = () => {
  const { t: translate } = useTranslation();

  const { state: onboardingState } = useContext(OnboardingContext);

  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isOpenRecV2, setIsOpenRecV2] = useState(false);

  const [recV2Token, setRecV2Token] = useState('');

  const [enablePasswordKMSEncryption] = useSetting(
    'app_features',
    'feature_toggle_enable_password_kms_encryption',
    false,
    false
  );

  const { data: publicKeyData } = useQuery(PASSWORD_PUBLIC_KEY, {
    enabled: !!enablePasswordKMSEncryption,
  });

  const allowSeparateEnv = onboardingState?.mainSettings?.feature_allow_separate_login_flow;
  const totpFeatureEnabled = onboardingState?.mainSettings?.feature_allow_totp;

  const { state } = useLocation();
  const history = useHistory();

  const { isUsEnv } = useUsEnv();
  const oauthReturnUrl = sessionStorage.getItem(
    isUsEnv ? OAUTH_SESSION_KEYS.US_RETURN_URL : OAUTH_SESSION_KEYS.ROW_RETURN_URL
  );
  const withdrawalReturnUrl = sessionStorage.getItem(WITHDRAWAL_DEEPLINK_SESSION_KEYS.RETURN_URL);
  const cardOrderReturnUrl = sessionStorage.getItem(CARD_ORDER_DEEPLINK_SESSION_KEYS.RETURN_URL);
  const topupReturnUrl = sessionStorage.getItem(TOPUP_DEEPLINK_SESSION_KEYS.RETURN_URL);
  const cashbackReturnUrl = sessionStorage.getItem(CASHBACK_DEEPLINK_SESSION_KEYS.RETURN_URL);
  const setPinReturnUrl = sessionStorage.getItem(SET_PIN_DEEPLINK_SESSION_KEYS.RETURN_URL);
  const manageAssetsReturnUrl = sessionStorage.getItem(
    MANAGE_ASSETS_DEEPLINK_SESSION_KEYS.RETURN_URL
  );

  const [accountRegion, setAccountRegion] = useState(ACCOUNT_REGIONS.UK_OR_EU);
  const [form, setForm] = useState({
    username: '',
    password: '',
  });

  useLayoutEffect(() => {
    setAccountRegion(getUrlEnvironment() || ACCOUNT_REGIONS.UK_OR_EU);
  }, []);

  useEffect(() => {
    if (getCacheRecaptchaV3failure() === 'true') {
      setIsOpenRecV2(true);
    }
  }, []);

  const { mutate: loginUser, isLoading } = useMutation(USER_LOGIN, {
    onSettled: (data, error) => {
      if (!error) {
        setIsOpenRecV2(false);
        sendStats(
          MXP_SIGN_IN_SUCCESS,
          { tfaRequired: data?.['2fa_required'], success: !error },
          FLOWS.SIGN_IN
        );

        if (data['2fa_required'] === true || (data?.totp_required === true && totpFeatureEnabled)) {
          resetUserSession();
          removeCachedPartialFlowToken();
          history.push(`/onboarding/login/2fa`, {
            ...form,
            ...data,
          });
        } else if (data?.data) {
          // Partial onboarding
          const { data: partialOnboardingData } = data;
          switch (partialOnboardingData?.step) {
            case PARTIAL_SIGN_UP_PHASES.PHONE_NUMBER:
              history.push(`/onboarding/signup/phone-number`, {
                email: partialOnboardingData?.email,
                password: partialOnboardingData?.password,
                countryOfResidenceId: partialOnboardingData?.country_of_residence,
                countryOfResidenceLabel: partialOnboardingData?.displayName,
                countryOfResidenceAlpha: partialOnboardingData?.iso3166alpha3,
                countryOfResidenceAlpha2: partialOnboardingData?.iso3166alpha2,
                callingCode: partialOnboardingData?.callingCode,
                allowMarketing: partialOnboardingData?.allow_marketing,
                verification_id: partialOnboardingData?.verification_id,
                partialSignupData: partialOnboardingData,
              });
              break;
            case PARTIAL_SIGN_UP_PHASES.PERSONAL_INFO:
              history.push(`/onboarding/signup/personal-information`, {
                countryOfResidenceAlpha: partialOnboardingData?.iso3166alpha3,
                countryOfResidenceAlpha2: partialOnboardingData?.iso3166alpha2,
                verification_id: partialOnboardingData?.verification_id,
                partialSignupData: partialOnboardingData,
              });
              break;
            case PARTIAL_SIGN_UP_PHASES.ADDITIONAL_DETAILS:
              history.push(`/onboarding/signup/additional-details`, {
                verification_id: partialOnboardingData?.verification_id,
                partialSignupData: partialOnboardingData,
              });
              break;
            case PARTIAL_SIGN_UP_PHASES.ADDRESS:
              history.push(`/onboarding/signup/address-information`, {
                countryOfResidenceLabel: partialOnboardingData?.displayName,
                countryOfResidenceAlpha: partialOnboardingData?.iso3166alpha3,
                countryOfResidenceAlpha2: partialOnboardingData?.iso3166alpha2,
                partialSignupData: partialOnboardingData,
                verification_id: partialOnboardingData?.verification_id,
                hideBackBtn: true,
              });
              break;
            case PARTIAL_SIGN_UP_PHASES.MAILING_ADDRESS:
              history.push(`/onboarding/signup/mailing-address`, {
                countryOfResidenceLabel: partialOnboardingData?.displayName,
                countryOfResidenceAlpha: partialOnboardingData?.iso3166alpha3,
                countryOfResidenceAlpha2: partialOnboardingData?.iso3166alpha2,
                partialSignupData: partialOnboardingData,
                verification_id: partialOnboardingData?.verification_id,
                hideBackBtn: true,
              });
              break;
            default:
              break;
          }
        } else {
          aliasUser(data);
          identifyUser(data);
          removeCachedPartialFlowToken();

          // OAuth 2 redirection after login
          if (oauthReturnUrl) {
            history.push(oauthReturnUrl);
            return;
          }
          if (withdrawalReturnUrl) {
            sessionStorage.removeItem(WITHDRAWAL_DEEPLINK_SESSION_KEYS.RETURN_URL);
            history.push(withdrawalReturnUrl);
            return;
          }
          if (cardOrderReturnUrl) {
            sessionStorage.removeItem(CARD_ORDER_DEEPLINK_SESSION_KEYS.RETURN_URL);
            history.push(cardOrderReturnUrl);
            return;
          }
          if (topupReturnUrl) {
            sessionStorage.removeItem(TOPUP_DEEPLINK_SESSION_KEYS.RETURN_URL);
            history.push(topupReturnUrl);
            return;
          }
          if (cashbackReturnUrl) {
            sessionStorage.removeItem(CASHBACK_DEEPLINK_SESSION_KEYS.RETURN_URL);
            history.push(cashbackReturnUrl);
            return;
          }
          if (setPinReturnUrl) {
            sessionStorage.removeItem(SET_PIN_DEEPLINK_SESSION_KEYS.RETURN_URL);
            history.push(setPinReturnUrl);
            return;
          }
          if (manageAssetsReturnUrl) {
            sessionStorage.removeItem(MANAGE_ASSETS_DEEPLINK_SESSION_KEYS.RETURN_URL);
            history.push(manageAssetsReturnUrl);
            return;
          }
          history.push(`/`, { showPromo: true });
        }
        clearRecaptchaV3failure();
      }
    },
    onError: (error) => {
      if (error?.response?.status === 307) {
        setIsOpenRecV2(true);
        cacheRecaptchaV3failure();
      } else {
        setIsOpenRecV2(false);
        clearRecaptchaV3failure();
        showAlert({ message: error.message, type: 'danger' });
      }
    },
  });

  useEffect(() => {
    if (state && state.resetPassword) {
      showAlert({ message: translate('password_reset_success_message'), type: 'success' });
      state.resetPassword = false;
    }
  }, [state, translate]);

  useEffect(() => {
    sendStats(MXP_SIGN_IN_VISIT, {}, FLOWS.SIGN_IN);
  }, []);

  const handleChange = (e, fieldName) => {
    if (!e) return;
    if (!e.target) {
      setForm({ ...form, [fieldName]: removeWhitespaces(e) });
    } else {
      const { name, value } = e.target;
      setForm({ ...form, [name]: removeWhitespaces(value) });
    }
  };

  const handleBlur = (e) => {
    if (e.target.name === 'username' && e.target.value && !validateEmail(e.target.value)) {
      if (e.target.className.indexOf('has-error') === -1) {
        e.target.className += ' has-error';
      }
    } else {
      clearFormError(e);
    }
    handleFormBlur(e);
  };

  const resetPasswordRedirect = () => {
    sendStats(MXP_RESET_PASSWORD_VISIT, {}, FLOWS.PASSWORD_RESET);
    history.push('/onboarding/reset-password');
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();

    try {
      const token = await executeRecaptcha(RECAPTCHA_EVENTS.LOGIN);
      if (!token) {
        return false;
      }

      if (enablePasswordKMSEncryption) {
        const encryptedPassword = await encryptWithPublicKey(
          publicKeyData.publicKey,
          form.password
        );
        loginUser({ ...form, password: encryptedPassword, token });
      } else {
        loginUser({ ...form, token });
      }
    } catch (err) {
      return false;
    }
    return true;
  };

  const onChangeRecV2 = async (tokenV2) => {
    setRecV2Token(tokenV2);
    if (enablePasswordKMSEncryption) {
      const encryptedPassword = await encryptWithPublicKey(publicKeyData.publicKey, form.password);
      loginUser({ ...form, password: encryptedPassword, token: tokenV2 });
    } else {
      loginUser({ ...form, token: tokenV2 });
    }
  };

  const renderButtons = () => (
    <>
      <div className="d-none d-md-block w-100">
        <button
          form="login-form"
          className="btn btn-primary w-100 text-bold"
          disabled={!form.password.length || !validateEmail(form.username)}
          type="submit"
          loading={isLoading ? 'true' : 'false'}>
          {translate('login_form_button_label')}
        </button>
        <p className="text-shadow text-bold mt-3">
          {translate('do_not_have_account_label')} &#x2022;{' '}
          <Link
            onClick={() => {
              sendStats(MXP_ONBOARDING_SIGN_UP_CREATE_ACCOUNT, {}, FLOWS.ONBOARDING);
            }}
            to="/onboarding/signup"
            className="link text-decoration-none">
            {translate('signup_title')}
          </Link>
        </p>
      </div>
      <div className="d-block d-md-none position-fixed bottom-0 bg-lighter w-100 py-3 px-3">
        <button
          form="login-form"
          className="btn btn-primary w-100 text-bold"
          disabled={!form.password.length || !validateEmail(form.username)}
          type="submit"
          loading={isLoading ? 'true' : 'false'}>
          {translate('login_form_button_label')}
        </button>
        <p className="text-shadow text-bold mt-3">
          {translate('do_not_have_account_label')} &#x2022;{' '}
          <Link
            onClick={() => {
              sendStats(MXP_ONBOARDING_SIGN_UP_CREATE_ACCOUNT, {}, FLOWS.ONBOARDING);
            }}
            to="/onboarding/signup"
            className="link text-decoration-none">
            {translate('signup_title')}
          </Link>
        </p>
      </div>
    </>
  );

  return (
    <>
      <LoginComponent
        form={form}
        renderButtons={renderButtons}
        allowSeparateEnv={allowSeparateEnv}
        handleChange={handleChange}
        handleBlur={handleBlur}
        handleSubmitForm={handleSubmitForm}
        revealPassword={revealPassword}
        resetPasswordRedirect={resetPasswordRedirect}
        accountRegion={accountRegion}
        clearFormError={clearFormError}
      />
      <RecaptchaModal
        onChange={onChangeRecV2}
        isLoading={isLoading}
        resetToken={() => setRecV2Token('')}
        token={recV2Token}
        setIsModalVisible={setIsOpenRecV2}
        isModalVisible={isOpenRecV2}
      />
    </>
  );
};

export default Login;
